// app/components/Watchlist.tsx
'use client'

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import styles from './Watchlist.module.css'
import { TickerItem } from '../types'
import { FavoritesPopup } from './FavoritesPopup'
import { useBinanceFavorites } from './hooks/useBinanceFavorites'
import { useBinanceWebsocket } from './hooks/useBinanceWebsocket'
import { useBinanceTickers, useBinanceSymbols } from './hooks/useBinanceData'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { useRouter } from 'next/navigation'
import { createVisualInteractionUtils, AnimationStatus } from '../../AITerminal/utils/visualInteractions'
import { useAITerminal } from '../../AITerminal/AITerminal'
import { useNinxFavorites } from './hooks/useNinxFavorites'
import { getSecureItem } from '../utils/secureStorage'

/**
 * @uiComponent
 * @name Watchlist
 * @description The Watchlist component displays real-time cryptocurrency market data on the Binance platform.
 * It allows users to view favorites, search through all available trading pairs, sort by various criteria,
 * and navigate to individual crypto pair pages. Features WebSocket-based real-time price updates,
 * infinite scroll for large datasets, and optimized favorites management.
 * @location Main left panel of the trading interface
 * @prop none - No external props, uses internal state management and hooks
 */

/**
 * @uiWorkflow
 * @name Add Cryptocurrency Pairs to Favorites on Binance
 * @description How to add crypto trading pairs to your watchlist favorites for quick access
 * @platform binance
 * @step 1 Navigate to the Watchlist tab in the trading terminal [component: Watchlist] [action: navigate]
 * @step 2 Switch to the "All" tab to browse available trading pairs [component: Watchlist] [action: click]
 * @step 3 Use the search bar to find specific cryptocurrency pairs [component: Watchlist] [action: input] [notes: e.g., BTC, ETH, ADA]
 * @step 4 Click the star icon next to any trading pair to add it to favorites [component: Watchlist] [action: click]
 * @step 5 Alternatively, click the "+ Add" button to open the favorites popup [component: Watchlist] [action: click]
 * @step 6 In the popup, search and select multiple pairs at once [component: FavoritesPopup] [action: select]
 * @step 7 Switch back to the "Favorites" tab to view your selected pairs [component: Watchlist] [action: click]
 * @step 8 Enjoy real-time price updates via WebSocket connection [component: Watchlist] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Monitor Real-time Crypto Prices on Binance
 * @description How to track live cryptocurrency price movements and market data
 * @platform binance
 * @step 1 Open the Watchlist component in the trading interface [component: Watchlist] [action: navigate]
 * @step 2 Add crypto pairs to favorites for focused monitoring [component: Watchlist] [action: click] [notes: Click star icons or use + Add button]
 * @step 3 View the "Favorites" tab for your selected trading pairs [component: Watchlist] [action: click]
 * @step 4 Observe real-time price updates with animated number changes [component: Watchlist] [action: observe] [notes: WebSocket updates prices automatically]
 * @step 5 Sort pairs by price, change percentage, or volume for analysis [component: Watchlist] [action: click] [notes: Click on column headers or filter chips]
 * @step 6 Use infinite scroll to load more trading pairs [component: Watchlist] [action: scroll]
 * @step 7 Click on any pair symbol to view detailed charts and trading options [component: Watchlist] [action: click]
 * @step 8 Monitor volume and price changes to identify trading opportunities [component: Watchlist] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Navigate to Crypto Trading Pair Details on Binance
 * @description How to access detailed cryptocurrency information and trading interface
 * @platform binance
 * @step 1 Navigate to the Watchlist in the trading terminal [component: Watchlist] [action: navigate]
 * @step 2 Browse through "Favorites" or "All" tabs to find your crypto pair [component: Watchlist] [action: click]
 * @step 3 Use the search bar to quickly locate a specific trading pair [component: Watchlist] [action: input] [notes: Search by base currency like BTC, ETH]
 * @step 4 Click on the trading pair symbol to navigate to its detail page [component: Watchlist] [action: click]
 * @step 5 The page will open with the trading pair's charts and order interface [component: TradingPairDetail] [action: observe]
 * @step 6 Access detailed price charts, order book, and trading history [component: TradingPairDetail] [action: navigate]
 * @step 7 Use the navigation to return to the watchlist view [component: Watchlist] [action: navigate]
 */

/**
 * @uiWorkflow
 * @name Sort and Filter Crypto Pairs on Binance Watchlist
 * @description How to organize and filter trading pairs for better market analysis
 * @platform binance
 * @step 1 Open the Watchlist component [component: Watchlist] [action: navigate]
 * @step 2 Use the search bar to filter pairs by currency name [component: Watchlist] [action: input] [notes: e.g., BTC, ETH, USDT]
 * @step 3 Click on "Name" filter chip to sort alphabetically [component: Watchlist] [action: click]
 * @step 4 Click on "Price" filter chip to sort by current price [component: Watchlist] [action: click]
 * @step 5 Use "Change %" filter to sort by percentage movement [component: Watchlist] [action: click]
 * @step 6 Apply "Volume" filter to see most actively traded pairs [component: Watchlist] [action: click]
 * @step 7 Toggle between ascending and descending order by clicking again [component: Watchlist] [action: click]
 * @step 8 Scroll down to load more trading pairs with infinite scroll [component: Watchlist] [action: scroll]
 * @step 9 Combine search and sorting for precise pair discovery [component: Watchlist] [action: combine]
 */

/**
 * @uiWorkflow
 * @name Use Infinite Scroll for Large Crypto Lists on Binance
 * @description How to efficiently browse through hundreds of trading pairs
 * @platform binance
 * @step 1 Access the Watchlist with the "All" tab selected [component: Watchlist] [action: click]
 * @step 2 Initial load shows the first 100 trading pairs [component: Watchlist] [action: observe]
 * @step 3 Scroll down towards the bottom of the list [component: Watchlist] [action: scroll]
 * @step 4 More pairs will automatically load as you approach the end [component: Watchlist] [action: observe]
 * @step 5 A loading indicator will show while new pairs are being loaded [component: Watchlist] [action: observe]
 * @step 6 Continue scrolling to load additional batches of 100 pairs [component: Watchlist] [action: scroll]
 * @step 7 Use search or sorting to narrow down the results if needed [component: Watchlist] [action: filter]
 */

/** Tabs at the top */
const TABS = ['Favorites', 'All']
const PAGE_SIZE = 100

export default function Watchlist() {
  const router = useRouter()
  const [activeTab, setActiveTab] = useState(TABS[0])
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState<{
    key: keyof TickerItem | 'symbol'
    direction: 'asc' | 'desc'
  }>({
    key: 'volume',
    direction: 'desc'
  })

  // infinite scroll state
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLTableRowElement>(null)

  // AI Terminal integration
  const { registerComponent } = useAITerminal()
  
  // Visual animation state
  const [isAnimating, setIsAnimating] = useState(false)
  const [lastAction, setLastAction] = useState<string>('')
  
  // Refs for visual interactions
  const containerRef = useRef<HTMLDivElement>(null)
  const favoritesTabRef = useRef<HTMLDivElement>(null)
  const allTabRef = useRef<HTMLDivElement>(null)
  const addFavoriteButtonRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  
  // Track registration status to prevent re-registration
  const isRegisteredRef = useRef(false)
  
  // Refs for execute functions to prevent event listener leaks
  const executeAddToFavoritesRef = useRef<any>(null)
  const executeGetFavoritesDataRef = useRef<any>(null)
  const executeGetTopMoversFromFavoritesRef = useRef<any>(null)
  const executeSearchAndAddSymbolRef = useRef<any>(null)
  const executeBulkTradeFromFavoritesRef = useRef<any>(null)
  
  // Create visual interaction utilities
  const visualUtils = createVisualInteractionUtils()

  const enabledConnector = getSecureItem('broker_enabled')
  const binanceFavorites = useBinanceFavorites()
  const ninxFavorites = useNinxFavorites()
  const activeFavoritesHook = enabledConnector === 'ninx' ? ninxFavorites : binanceFavorites
  const { favorites, toggleFavorite, addFavorite } = activeFavoritesHook
  
  // Use React Query for initial data fetching only
  const { data: initialTickers = [], isLoading: isInitialLoading, error } = useBinanceTickers()
  const { symbols: allSymbols } = useBinanceSymbols()

  // Local state for tickers that gets updated by WebSocket (like original)
  const [tickers, setTickers] = useState<TickerItem[]>([])

  // Update local tickers when initial data is loaded
  React.useEffect(() => {
    if (initialTickers.length > 0) {
      setTickers(initialTickers)
    }
  }, [initialTickers])

  // Compute filtered and sorted tickers using useMemo (like original)
  const filteredTickers = useMemo(() => {
    let list = tickers
    
    // Filter by favorites tab
    if (activeTab === 'Favorites') {
      list = list.filter(t => favorites.includes(t.symbol))
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      list = list.filter(t => t.symbol.toLowerCase().includes(term))
    }
    
    // Sort the list
    return [...list].sort((a, b) => {
      const { key, direction } = sortConfig
      if (key === 'symbol') {
        return direction === 'asc'
          ? a.symbol.localeCompare(b.symbol)
          : b.symbol.localeCompare(a.symbol)
      }
      const va = a[key] as any, vb = b[key] as any
      return direction === 'asc' ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1)
    })
  }, [tickers, activeTab, favorites, searchTerm, sortConfig])

  // Compute paged tickers using useMemo
  const pagedTickers = useMemo(() => {
    return filteredTickers.slice(0, visibleCount)
  }, [filteredTickers, visibleCount])

  // Reset visible count when tab, search or sort changes
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [activeTab, searchTerm, sortConfig])

  // Stable WebSocket symbols computation
  const visibleSymbols = useMemo(
    () => pagedTickers.map(t => t.symbol.replace('USDT', '')),
    [pagedTickers.map(t => t.symbol).join(',')] // Only change when actual symbols change
  )
  
  // Stable WebSocket callback with original efficient logic
  const onTickerUpdate = useCallback((updates: any[]) => {
    if (!isInitialLoading && tickers.length) {
      setTickers(prev => {
        const copy = [...prev]
        updates.forEach(u => {
          const idx = copy.findIndex(x => x.symbol === u.symbol)
          if (idx >= 0) {
            copy[idx] = {
              ...copy[idx],
              price: u.price,
              priceChangePercent: u.priceChangePercent
            }
          }
        })
        return copy // NO RE-SORTING! Preserves original sort order
      })
    }
  }, [isInitialLoading, tickers.length])

  // Use the existing WebSocket hook
  useBinanceWebsocket({
    symbols: visibleSymbols,
    onTickerUpdate
  })
  
   // keep the config object stable between renders
  const wsConfig = useMemo(() => ({
      symbols: visibleSymbols,
      onTickerUpdate
    }), [visibleSymbols, onTickerUpdate])
  
    useBinanceWebsocket(wsConfig)

  // 6. window-scroll infinite loader
  useEffect(() => {
    if (!sentinelRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !loadingMore &&
            visibleCount < filteredTickers.length
          ) {
            setLoadingMore(true)
            setVisibleCount((vc) =>
              Math.min(vc + PAGE_SIZE, filteredTickers.length)
            )
            // Let the spinner show for a moment
            setTimeout(() => {
              setLoadingMore(false)
            }, 300)
          }
        })
      },
      {
        root: containerRef.current, // `null` means observe against viewport; if your CSS makes a scrollable container,
                    // swap `root: containerRef.current` if needed.
        rootMargin: '50px',
        threshold: 0.1,
      }
    )

    observer.observe(sentinelRef.current)
    return () => {
      observer.disconnect()
    }
  }, [filteredTickers.length, loadingMore, visibleCount])

  const handleSort = (key: keyof TickerItem | 'symbol') => {
    setSortConfig(cfg => ({
      key,
      direction:
        cfg.key === key && cfg.direction === 'asc' ? 'desc' : 'asc'
    }))
  }
  
  const handleSymbolClick = (sym: string) => {
    window.location.href = `/${sym.toLowerCase()}`
  }

  // Visual interaction handlers for AI Terminal
  const executeAddToFavorites = useCallback(async (params: any) => {
    console.log('🔧 Starting executeAddToFavorites with params:', params)
    setIsAnimating(true)
    setLastAction(`⭐ Adding symbols to favorites: ${params.symbols.join(', ')}`)
    
    // Add timeout protection to prevent browser freezing
    const timeoutId = setTimeout(() => {
      console.log('⚠️ AddToFavorites operation timed out after 15 seconds')
      setIsAnimating(false)
      setLastAction('⚠️ Operation timed out')
    }, 15000)
    
    try {
      // Scroll the component into view first
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
        await new Promise(resolve => setTimeout(resolve, 800))
      }
      
      // Switch to All tab to search for symbols
      if (activeTab !== 'All') {
        setLastAction('🔄 Switching to All tab to search for symbols...')
        setActiveTab('All')
        await visualUtils.highlightElement(allTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      const addedSymbols = []
      const notFoundSymbols = []
      
      for (const symbol of params.symbols) {
        const formattedSymbol = symbol.toUpperCase()
        console.log(`🔍 Processing symbol: ${formattedSymbol}`)
        
        // Better symbol matching logic
        let matchedSymbol = null
        
        // Direct match first
        if (allSymbols.includes(formattedSymbol)) {
          matchedSymbol = formattedSymbol
        }
        // Add USDT if not present
        else if (allSymbols.includes(formattedSymbol + 'USDT')) {
          matchedSymbol = formattedSymbol + 'USDT'
        }
        // Try finding symbols that contain this term
        else {
          matchedSymbol = allSymbols.find(s => 
            s.toUpperCase().includes(formattedSymbol) || 
            s.toUpperCase().includes(formattedSymbol.replace('USDT', ''))
          )
        }
        
        if (matchedSymbol) {
          setLastAction(`⭐ Adding ${matchedSymbol} to favorites...`)
          console.log(`✅ Found matching symbol: ${matchedSymbol}`)
          
          if (!favorites.includes(matchedSymbol)) {
            addFavorite(matchedSymbol)
            addedSymbols.push(matchedSymbol)
            console.log(`✅ Added ${matchedSymbol} to favorites`)
          } else {
            console.log(`ℹ️ ${matchedSymbol} already in favorites`)
            addedSymbols.push(matchedSymbol) // Still count as "added" for user feedback
          }
          
          await new Promise(resolve => setTimeout(resolve, 300))
        } else {
          console.log(`❌ No matching symbol found for: ${formattedSymbol}`)
          notFoundSymbols.push(formattedSymbol)
        }
      }
      
      // Switch back to Favorites tab to show results
      if (addedSymbols.length > 0) {
        setLastAction('🏠 Switching to Favorites tab to show results...')
        setActiveTab('Favorites')
        await visualUtils.highlightElement(favoritesTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      const message = addedSymbols.length > 0 
        ? `✅ Added ${addedSymbols.length} symbols to favorites: ${addedSymbols.join(', ')}`
        : `❌ No symbols were added`
      
      setLastAction(message)
      
      return {
        success: addedSymbols.length > 0,
        addedSymbols,
        notFoundSymbols,
        message,
        favoritesCount: favorites.length + addedSymbols.length
      }
      
    } catch (error) {
      setLastAction(`❌ Error adding to favorites: ${error}`)
      return {
        success: false,
        error: `Failed to add symbols to favorites: ${error}`
      }
    } finally {
      clearTimeout(timeoutId) // Clear the timeout if operation completes
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 2000)
    }
  }, [activeTab, visualUtils, allSymbols, favorites.length, addFavorite])

  const executeGetFavoritesData = useCallback(async (params: any) => {
    setIsAnimating(true)
    setLastAction('📊 Getting favorites data...')
    
    try {
      // Switch to Favorites tab if not already there
      if (activeTab !== 'Favorites') {
        setLastAction('🏠 Switching to Favorites tab...')
        setActiveTab('Favorites')
        await visualUtils.highlightElement(favoritesTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Apply sorting if specified
      if (params.sortBy && params.sortDirection) {
        setLastAction(`🔄 Sorting by ${params.sortBy} (${params.sortDirection})...`)
        setSortConfig({
          key: params.sortBy,
          direction: params.sortDirection
        })
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Debug logging
      console.log('🔍 Debug - Favorites array:', favorites)
      console.log('🔍 Debug - All tickers count:', tickers.length)
      console.log('🔍 Debug - FilteredTickers count:', filteredTickers.length)
      console.log('🔍 Debug - Sample tickers:', tickers.slice(0, 3))
      
      // Always use the full tickers array for favorites data (not filteredTickers which may be searched/sorted)
      let favoritesData = tickers.filter(t => favorites.includes(t.symbol))
      console.log('🔍 Debug - Matched favorites from all tickers:', favoritesData.map(t => ({
        symbol: t.symbol, 
        price: t.price, 
        change: t.priceChangePercent,
        volume: t.volume
      })))
      
      // If we still don't have data, it might be because tickers haven't loaded yet
      if (favoritesData.length === 0 && favorites.length > 0 && tickers.length === 0) {
        console.log('🔍 Debug - Tickers not loaded yet, returning empty with warning')
        return {
          success: false,
          error: 'Market data not loaded yet. Please try again in a moment.',
          favoritesData: [],
          favorites,
          count: 0,
          totalFavorites: favorites.length
        }
      }
      
      // Only create fallback data if we have favorites but no matching tickers
      if (favoritesData.length === 0 && favorites.length > 0 && tickers.length > 0) {
        console.log('🔍 Debug - No matching tickers found for favorites, creating placeholder data')
        favoritesData = favorites.map(symbol => ({
          symbol,
          originalSymbol: symbol,
          exchange: 'binance',
          price: 0,
          priceChangePercent: 0,
          volume: 0
        }))
      }
      
      setLastAction(`✅ Retrieved ${favoritesData.length} favorites (out of ${favorites.length} total favorites)`)
      
      return {
        success: true,
        favoritesData,
        favorites, // Also return the raw favorites array for debugging
        count: favoritesData.length,
        totalFavorites: favorites.length,
        sortBy: params.sortBy || sortConfig.key,
        sortDirection: params.sortDirection || sortConfig.direction
      }
      
    } catch (error) {
      setLastAction(`❌ Error getting favorites data: ${error}`)
      return {
        success: false,
        error: `Failed to get favorites data: ${error}`
      }
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 2000)
    }
  }, [activeTab, visualUtils, tickers, favorites])

  const executeGetTopMoversFromFavorites = useCallback(async (params: any) => {
    setIsAnimating(true)
    setLastAction(`🚀 Getting top ${params.type || 'gainers'} from favorites...`)
    
    try {
      // Switch to Favorites tab if not already there
      if (activeTab !== 'Favorites') {
        setLastAction('🏠 Switching to Favorites tab...')
        setActiveTab('Favorites')
        await visualUtils.highlightElement(favoritesTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Get favorites data from all tickers and sort by price change
      const favoritesData = tickers.filter(t => favorites.includes(t.symbol))
      const sortedData = favoritesData.sort((a, b) => {
        if (params.type === 'losers') {
          return a.priceChangePercent - b.priceChangePercent // Ascending for losers
        } else {
          return b.priceChangePercent - a.priceChangePercent // Descending for gainers
        }
      })
      
      const topMovers = sortedData.slice(0, params.limit || 5)
      
      setLastAction(`✅ Found ${topMovers.length} top ${params.type || 'gainers'}`)
      
      return {
        success: true,
        type: params.type || 'gainers',
        topMovers,
        count: topMovers.length,
        limit: params.limit || 5
      }
      
    } catch (error) {
      setLastAction(`❌ Error getting top movers: ${error}`)
      return {
        success: false,
        error: `Failed to get top movers: ${error}`
      }
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 2000)
    }
  }, [activeTab, visualUtils, tickers, favorites])

  const executeSearchAndAddSymbol = useCallback(async (params: any) => {
    setIsAnimating(true)
    setLastAction(`🔍 Searching for symbol: ${params.searchTerm}`)
    
    try {
      // Switch to All tab to search
      if (activeTab !== 'All') {
        setLastAction('🔄 Switching to All tab to search...')
        setActiveTab('All')
        await visualUtils.highlightElement(allTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Use improved search input functionality
      setLastAction(`🔍 Searching for "${params.searchTerm}"...`)
      console.log('🔍 Debug - Search term:', params.searchTerm)
      
      // Use the visual utils to type into search input
      const searchSuccess = await visualUtils.typeIntoSearchInput(params.searchTerm)
      
      if (searchSuccess) {
        setSearchTerm(params.searchTerm)
      } else {
        // Fallback to direct state update
        setSearchTerm(params.searchTerm)
      }
      
      // Wait for search to process
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Find matching symbols
      const allTickersMatchingSearch = tickers.filter(t => 
        t.symbol.toLowerCase().includes(params.searchTerm.toLowerCase())
      )
      
      const filteredMatchingSearch = filteredTickers.filter(t => 
        t.symbol.toLowerCase().includes(params.searchTerm.toLowerCase())
      )
      
      console.log('🔍 Debug - All tickers matching search:', allTickersMatchingSearch.length)
      console.log('🔍 Debug - Filtered tickers matching search:', filteredMatchingSearch.length)
      console.log('🔍 Debug - First few matches:', allTickersMatchingSearch.slice(0, 3).map(t => t.symbol))
      
      const searchResults = allTickersMatchingSearch.length > 0 ? allTickersMatchingSearch : filteredMatchingSearch
      
      if (searchResults.length > 0 && params.addToFavorites !== false) {
        const symbolToAdd = searchResults[0].symbol
        setLastAction(`⭐ Adding ${symbolToAdd} to favorites...`)
        
        // Check if already in favorites
        if (!favorites.includes(symbolToAdd)) {
          addFavorite(symbolToAdd)
          console.log('✅ Debug - Added to favorites:', symbolToAdd)
        } else {
          console.log('ℹ️ Debug - Already in favorites:', symbolToAdd)
        }
        
        // Switch to favorites to show the result
        setLastAction('🏠 Switching to Favorites to show result...')
        setActiveTab('Favorites')
        await visualUtils.highlightElement(favoritesTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      } else {
        console.log('❌ Debug - No search results or addToFavorites is false')
      }
      
      // Clear search
      setSearchTerm('')
      
      const message = searchResults.length > 0 
        ? `✅ Found ${searchResults.length} matching symbols`
        : `❌ No symbols found for "${params.searchTerm}"`
      
      setLastAction(message)
      
      return {
        success: searchResults.length > 0,
        searchTerm: params.searchTerm,
        results: searchResults,
        addedToFavorites: searchResults.length > 0 && params.addToFavorites !== false,
        message
      }
      
    } catch (error) {
      setLastAction(`❌ Error searching for symbol: ${error}`)
      return {
        success: false,
        error: `Failed to search for symbol: ${error}`
      }
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
        setSearchTerm('') // Clear search term
      }, 2000)
    }
  }, [activeTab, visualUtils, tickers, allSymbols, addFavorite])

  const executeBulkTradeFromFavorites = useCallback(async (params: any) => {
    setIsAnimating(true)
    setLastAction(`💰 Starting bulk ${params.side} trading for favorites...`)
    
    try {
      // Switch to Favorites tab first
      if (activeTab !== 'Favorites') {
        setLastAction('🏠 Switching to Favorites tab...')
        setActiveTab('Favorites')
        await visualUtils.highlightElement(favoritesTabRef as React.RefObject<HTMLElement>, 800)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Get symbols to trade
      let symbolsToTrade = []
      
      if (params.symbols && params.symbols.length > 0) {
        // Use specific symbols provided
        symbolsToTrade = params.symbols
      } else {
        // Use favorites with filtering from all tickers
        console.log('💰 Debug - Bulk Trade - Favorites:', favorites)
        console.log('💰 Debug - Bulk Trade - All tickers count:', tickers.length)
        const favoritesData = tickers.filter(t => favorites.includes(t.symbol))
        console.log('💰 Debug - Bulk Trade - Matched favorites:', favoritesData.map(t => t.symbol))
        
        if (params.filterBy === 'gainers') {
          const gainers = favoritesData
            .filter(t => t.priceChangePercent > 0)
            .sort((a, b) => b.priceChangePercent - a.priceChangePercent)
            .slice(0, params.limit || 10)
          symbolsToTrade = gainers.map(t => t.symbol)
        } else if (params.filterBy === 'losers') {
          const losers = favoritesData
            .filter(t => t.priceChangePercent < 0)
            .sort((a, b) => a.priceChangePercent - b.priceChangePercent)
            .slice(0, params.limit || 10)
          symbolsToTrade = losers.map(t => t.symbol)
        } else {
          // All favorites
          symbolsToTrade = favoritesData.slice(0, params.limit || 10).map(t => t.symbol)
        }
      }
      
      if (symbolsToTrade.length === 0) {
        setLastAction('❌ No symbols found for bulk trading')
        return {
          success: false,
          error: 'No symbols found for bulk trading',
          symbolsCount: 0
        }
      }
      
      setLastAction(`📋 Found ${symbolsToTrade.length} symbols for bulk trading`)
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Execute trades for each symbol
      const tradeResults = []
      let successCount = 0
      
      for (const symbol of symbolsToTrade) {
        setLastAction(`💰 Placing ${params.side} order for ${symbol} (${params.quantityPerSymbol})`)
        
        try {
          // Trigger individual trading order through place_order tool
          const tradeEvent = new CustomEvent('realPlaceOrder', {
            detail: {
              symbol,
              side: params.side,
              quantity: params.quantityPerSymbol,
              type: params.type || 'MARKET'
            }
          })
          
          window.dispatchEvent(tradeEvent)
          
          tradeResults.push({
            symbol,
            success: true,
            quantity: params.quantityPerSymbol,
            side: params.side
          })
          successCount++
          
          await new Promise(resolve => setTimeout(resolve, 1000)) // Delay between orders
          
        } catch (error) {
          tradeResults.push({
            symbol,
            success: false,
            error: `Failed to place order: ${error}`
          })
        }
      }
      
      setLastAction(`✅ Bulk trading completed: ${successCount}/${symbolsToTrade.length} orders placed`)
      
      return {
        success: successCount > 0,
        totalSymbols: symbolsToTrade.length,
        successCount,
        failCount: symbolsToTrade.length - successCount,
        tradeResults,
        message: `Bulk ${params.side} trading completed: ${successCount}/${symbolsToTrade.length} orders placed`
      }
      
    } catch (error) {
      setLastAction(`❌ Error in bulk trading: ${error}`)
      return {
        success: false,
        error: `Failed to execute bulk trading: ${error}`
      }
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 3000)
    }
  }, [activeTab, visualUtils, tickers, favorites])

  // Register this component with AI Terminal once
  useEffect(() => {
    if (!isRegisteredRef.current) {
      registerComponent('watchlist', {
        id: 'watchlist',
        type: 'Watchlist',
        name: 'Cryptocurrency Watchlist',
        capabilities: [
          {
            action: 'add_to_favorites',
            description: 'Add symbols to favorites watchlist',
            parameters: [
              { name: 'symbols', type: 'array', description: 'Array of symbols to add' }
            ]
          },
          {
            action: 'get_favorites_data',
            description: 'Get current favorites data',
            parameters: [
              { name: 'sortBy', type: 'string', description: 'Sort criteria' },
              { name: 'sortDirection', type: 'string', description: 'Sort direction' }
            ]
          },
          {
            action: 'get_top_movers_favorites',
            description: 'Get top movers from favorites',
            parameters: [
              { name: 'type', type: 'string', description: 'gainers or losers' },
              { name: 'limit', type: 'number', description: 'Number of results' }
            ]
          },
          {
            action: 'search_and_add_symbol',
            description: 'Search for symbol and optionally add to favorites',
            parameters: [
              { name: 'searchTerm', type: 'string', description: 'Symbol to search for' },
              { name: 'addToFavorites', type: 'boolean', description: 'Whether to add to favorites' }
            ]
          },
          {
            action: 'bulk_trade_favorites',
            description: 'Place bulk trading orders for favorites',
            parameters: [
              { name: 'side', type: 'string', description: 'BUY or SELL' },
              { name: 'quantityPerSymbol', type: 'number', description: 'Amount per symbol' },
              { name: 'type', type: 'string', description: 'Order type' },
              { name: 'filterBy', type: 'string', description: 'Filter criteria' },
              { name: 'limit', type: 'number', description: 'Max symbols to trade' }
            ]
          }
        ],
        methods: {
          add_to_favorites: executeAddToFavorites,
          get_favorites_data: executeGetFavoritesData,
          get_top_movers_favorites: executeGetTopMoversFromFavorites,
          search_and_add_symbol: executeSearchAndAddSymbol,
          bulk_trade_favorites: executeBulkTradeFromFavorites
        }
      })
      isRegisteredRef.current = true
    }
  }, [registerComponent])

  // Sync refs whenever execute functions change
  useEffect(() => {
    executeAddToFavoritesRef.current = executeAddToFavorites
    executeGetFavoritesDataRef.current = executeGetFavoritesData
    executeGetTopMoversFromFavoritesRef.current = executeGetTopMoversFromFavorites
    executeSearchAndAddSymbolRef.current = executeSearchAndAddSymbol
    executeBulkTradeFromFavoritesRef.current = executeBulkTradeFromFavorites
  }, [executeAddToFavorites, executeGetFavoritesData, executeGetTopMoversFromFavorites, executeSearchAndAddSymbol, executeBulkTradeFromFavorites])

  // Setup event listeners with stable handlers - register only once
  useEffect(() => {
    // Listen for AI Terminal events
    const handleAddToFavorites = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('⭐ Watchlist: Received add to favorites event:', customEvent.detail)
      console.log('⭐ Watchlist: Event type:', customEvent.type)
      console.log('⭐ Watchlist: Current state - activeTab:', activeTab, 'allSymbols:', allSymbols.length)
      try {
        const result = await executeAddToFavoritesRef.current(customEvent.detail)
        console.log('⭐ Watchlist: Add to favorites result:', result)
        window.dispatchEvent(new CustomEvent('addToFavoritesResult', { detail: result }))
      } catch (error) {
        console.error('⭐ Watchlist: Add to favorites error:', error)
        window.dispatchEvent(new CustomEvent('addToFavoritesResult', { 
          detail: { success: false, error: `Failed to add to favorites: ${error}` }
        }))
      }
    }

    const handleGetFavoritesData = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('📊 Watchlist: Received get favorites data event:', customEvent.detail)
      console.log('📊 Watchlist: Event type:', customEvent.type)
      console.log('📊 Watchlist: Current state - activeTab:', activeTab, 'allSymbols:', allSymbols.length)
      try {
        const result = await executeGetFavoritesDataRef.current(customEvent.detail)
        console.log('📊 Watchlist: Get favorites data result:', result)
        
        // Send back result with actual data
        window.dispatchEvent(new CustomEvent('getFavoritesDataResult', { 
          detail: {
            ...result,
            eventId: customEvent.detail.eventId // Pass through event ID if provided
          }
        }))
      } catch (error) {
        console.error('📊 Watchlist: Get favorites data error:', error)
        window.dispatchEvent(new CustomEvent('getFavoritesDataResult', { 
          detail: { 
            success: false, 
            error: `Failed to get favorites data: ${error}`,
            eventId: customEvent.detail.eventId
          }
        }))
      }
    }

    const handleGetTopMoversFromFavorites = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🚀 Watchlist: Received get top movers event:', customEvent.detail)
      try {
        const result = await executeGetTopMoversFromFavoritesRef.current(customEvent.detail)
        console.log('🚀 Watchlist: Get top movers result:', result)
        
        // Send back result with actual data
        window.dispatchEvent(new CustomEvent('getTopMoversFromFavoritesResult', { 
          detail: {
            ...result,
            eventId: customEvent.detail.eventId // Pass through event ID if provided
          }
        }))
      } catch (error) {
        console.error('🚀 Watchlist: Get top movers error:', error)
        window.dispatchEvent(new CustomEvent('getTopMoversFromFavoritesResult', { 
          detail: { 
            success: false, 
            error: `Failed to get top movers: ${error}`,
            eventId: customEvent.detail.eventId
          }
        }))
      }
    }

    const handleSearchAndAddSymbol = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🔍 Watchlist: Received search and add event:', customEvent.detail)
      try {
        const result = await executeSearchAndAddSymbolRef.current(customEvent.detail)
        window.dispatchEvent(new CustomEvent('searchAndAddSymbolResult', { detail: result }))
      } catch (error) {
        window.dispatchEvent(new CustomEvent('searchAndAddSymbolResult', { 
          detail: { success: false, error: `Failed to search and add symbol: ${error}` }
        }))
      }
    }

    const handleBulkTradeFromFavorites = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('💰 Watchlist: Received bulk trade event:', customEvent.detail)
      try {
        const result = await executeBulkTradeFromFavoritesRef.current(customEvent.detail)
        window.dispatchEvent(new CustomEvent('bulkTradeFromFavoritesResult', { detail: result }))
      } catch (error) {
        window.dispatchEvent(new CustomEvent('bulkTradeFromFavoritesResult', { 
          detail: { success: false, error: `Failed to execute bulk trade: ${error}` }
        }))
      }
    }

    window.addEventListener('addToFavorites', handleAddToFavorites)
    window.addEventListener('getFavoritesData', handleGetFavoritesData)
    window.addEventListener('getTopMoversFromFavorites', handleGetTopMoversFromFavorites)
    window.addEventListener('searchAndAddSymbol', handleSearchAndAddSymbol)
    window.addEventListener('bulkTradeFromFavorites', handleBulkTradeFromFavorites)

    return () => {
      window.removeEventListener('addToFavorites', handleAddToFavorites)
      window.removeEventListener('getFavoritesData', handleGetFavoritesData)
      window.removeEventListener('getTopMoversFromFavorites', handleGetTopMoversFromFavorites)
      window.removeEventListener('searchAndAddSymbol', handleSearchAndAddSymbol)
      window.removeEventListener('bulkTradeFromFavorites', handleBulkTradeFromFavorites)
    }
  }, []) // Empty dependency array - stable event listeners that use refs

  return (
    <div ref={containerRef} className={`${styles.watchlistContainer} ${styles.themeVariables}`}>
      <div className={styles.tabHeader}>
        {TABS.map(tab => (
          <div
            key={tab}
            ref={tab === 'Favorites' ? favoritesTabRef : tab === 'All' ? allTabRef : null}
            className={`${styles.tab} ${
              tab === activeTab ? styles.active : ''
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
        <button
          ref={addFavoriteButtonRef}
          className={styles.addFavoriteButton}
          onClick={() => setIsPopupOpen(true)}
        >
          + Add
        </button>
      </div>
      
      {/* Animation Status */}
      <AnimationStatus isAnimating={isAnimating} lastAction={lastAction} />

      <div className={styles.tabContent}>
        {isInitialLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading market data...</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{error instanceof Error ? error.message : String(error)}</p>
            <button
              onClick={() => setVisibleCount(PAGE_SIZE)}
              className={styles.retryButton}
            >
              Retry
            </button>
          </div>
        ) : filteredTickers.length === 0 ? (
          <div className={styles.emptyState}>
            {activeTab === 'Favorites' ? (
              <>
                <p>No favorites added yet</p>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className={styles.addButton}
                >
                  Add Favorites
                </button>
              </>
            ) : (
              <p>No data available</p>
            )}
          </div>
        ) : (
          <>
            {/* search + sort */}
            <div className={styles.tableControls}>
              <div className={styles.searchContainer}>
                <MagnifyingGlassIcon
                  className={styles.searchIcon}
                  width={18}
                  height={18}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search symbols..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              <div className={styles.filterChips}>
                {(
                  ['symbol', 'price', 'priceChangePercent', 'volume'] as const
                ).map(key => (
                  <div
                    key={key}
                    className={`${styles.filterChip} ${
                      sortConfig.key === key ? styles.active : ''
                    }`}
                    onClick={() => handleSort(key)}
                  >
                    {key === 'symbol'
                      ? 'Name'
                      : key === 'price'
                      ? 'Price'
                      : key === 'priceChangePercent'
                      ? 'Change %'
                      : 'Volume'}
                    {sortConfig.key === key &&
                      (sortConfig.direction === 'asc' ? (
                        <ArrowUpIcon className={styles.chipIcon} />
                      ) : (
                        <ArrowDownIcon className={styles.chipIcon} />
                      ))}
                    {key === 'price' && (
                      <CurrencyDollarIcon className={styles.chipIcon} />
                    )}
                    {key === 'volume' && (
                      <ChartBarIcon className={styles.chipIcon} />
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.resultsCount}>
                {filteredTickers.length} of {tickers.length}
              </div>
            </div>

            {/* table */}
            <div className={styles.tickersTable}>
              <table>
                <tbody>
                  {pagedTickers.map(t => (
                    <tr key={t.symbol} className={styles.tickerRow}>
                      <td
                        className={`${styles.symbolCell} ${styles.clickable}`}
                        onClick={() => handleSymbolClick(t.symbol)}
                      >
                        {t.symbol}
                      </td>
                      <td className={styles.priceCell}>
                        $<AnimatedNumber
                          value={t.price}
                          formatOptions={{
                            minimumFractionDigits:
                              t.price < 1 ? 8 : 2,
                            maximumFractionDigits:
                              t.price < 1 ? 8 : 2
                          }}
                          digitHeight={20}
                          duration={0.3}
                        />
                      </td>
                      <td
                        className={`${styles.changeCell} ${
                          t.priceChangePercent >= 0
                            ? styles.profit
                            : styles.loss
                        }`}
                      >
                        <AnimatedNumber
                          value={t.priceChangePercent}
                          formatOptions={{
                            signDisplay: 'exceptZero',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }}
                          digitHeight={20}
                          duration={0.3}
                        />
                        %
                      </td>
                      <td className={styles.volumeCell}>
                        ${t.volume.toLocaleString()}
                      </td>
                      <td className={styles.favoriteCell}>
                        <button
                          className={styles.favoriteButton}
                          onClick={() => toggleFavorite(t.symbol)}
                        >
                          {favorites.includes(t.symbol) ? (
                            <StarIconSolid
                              className={styles.starIconFilled}
                              width={18}
                              height={18}
                            />
                          ) : (
                            <StarIconOutline
                              className={styles.starIcon}
                              width={18}
                              height={18}
                            />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* sentinel row */}
                  <tr ref={sentinelRef} style={{ height: 1 }}>
                    <td colSpan={5} />
                  </tr>

                  {/* loading-more row */}
                  {loadingMore && (
                    <tr>
                      <td colSpan={5} className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <span style={{ marginLeft: 8 }}>Loading more...</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <FavoritesPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onAddFavorite={symbol => {
          toggleFavorite(symbol)
          setIsPopupOpen(false)
        }}
        currentFavorites={favorites}
        availableSymbols={allSymbols}
      />
    </div>
  )
}
